import { Flex, theme, Typography } from 'antd';
import { Skeleton } from '../Skeleton';
import { radius } from '../../theme/radius';

export interface StatSummaryItem {
  label: string;
  /** Already formatted for display — the caller owns units and rounding. */
  value: string;
}

export interface StatSummaryProps {
  heading: string;
  subtitle?: string;
  /** Tiles share the row evenly and wrap when they run out of width. */
  stats: StatSummaryItem[];
  loading?: boolean;
}

/**
 * A row of headline figures — a pulse of the numbers, with the full breakdown
 * living on a dedicated analytics page.
 */
export const StatSummary = ({
  heading,
  subtitle,
  stats,
  loading,
}: StatSummaryProps) => {
  const { token } = theme.useToken();

  return (
    <Flex vertical gap={12}>
      <Flex vertical gap={2}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {heading}
        </Typography.Title>
        {subtitle ? (
          <Typography.Text type="secondary">{subtitle}</Typography.Text>
        ) : null}
      </Flex>
      <Flex gap={12} wrap>
        {stats.map((stat) => (
          <Flex
            key={stat.label}
            vertical
            gap={4}
            flex="1 1 130px"
            style={{
              minWidth: 130,
              padding: '14px 16px',
              border: `1px solid ${token.colorBorderSecondary}`,
              borderRadius: radius.box,
              background: token.colorFillQuaternary,
            }}
          >
            {loading ? (
              <Skeleton variant="text" rows={1} width="60%" height={24} />
            ) : (
              <Typography.Text
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {stat.value}
              </Typography.Text>
            )}
            <Typography.Text
              type="secondary"
              style={{ fontSize: token.fontSizeSM, fontWeight: 600 }}
            >
              {stat.label}
            </Typography.Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
